import { useEffect, useRef, useState } from "react";

export interface MeasuredBox {
	width: number;
	height: number;
}

const px = (value: string): number => Number.parseFloat(value) || 0;

/**
 * The box a wheel may actually draw in. The PARENT is measured, never the component's own root — the
 * root's height comes from the SVG, so observing it would feed the size back into itself. The
 * parent's padding and its other children (the intervention icon row) are subtracted, because that
 * space is reserved and not drawable.
 */
export const useMeasuredBox = () => {
	const ref = useRef<HTMLDivElement>(null);
	const [box, setBox] = useState<MeasuredBox>({ width: 0, height: 0 });

	useEffect(() => {
		const node = ref.current?.parentElement;
		if (!node) return;

		const measure = () => {
			const rect = node.getBoundingClientRect();
			const style = typeof getComputedStyle === "function" ? getComputedStyle(node) : null;
			const padX = style ? px(style.paddingLeft) + px(style.paddingRight) : 0;
			const padY = style ? px(style.paddingTop) + px(style.paddingBottom) : 0;
			const gap = style ? px(style.rowGap) : 0;

			const siblings = Array.from(node.children).filter((child) => child !== ref.current);
			const reserved = siblings.reduce((sum, child) => sum + child.getBoundingClientRect().height, 0) + gap * siblings.length;

			const width = Math.max(0, (rect.width || node.clientWidth || 0) - padX);
			const height = Math.max(0, (rect.height || node.clientHeight || 0) - padY - reserved);
			setBox((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
		};
		measure();

		if (typeof ResizeObserver === "undefined") return;

		// The siblings are observed too — the icon row changes height with the panel, not with the stage.
		const observer = new ResizeObserver(measure);
		observer.observe(node);
		Array.from(node.children).forEach((child) => observer.observe(child));
		return () => observer.disconnect();
	}, []);

	return { ref, box };
};

export default useMeasuredBox;
